import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import {
  Activity,
  Aperture,
  Boxes,
  ClipboardList,
  Coins,
  FileClock,
  Film,
  GitBranch,
  Image,
  KeyRound,
  LogOut,
  Menu,
  Moon,
  ReceiptText,
  ServerCog,
  ShieldCheck,
  Sun,
  Users,
  X
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { sessionQuery } from "../lib/queries";
import { formatPoints, IconButton } from "./ui";

const userNavigation = [
  { to: "/tasks", label: "任务", icon: ClipboardList },
  { to: "/create/image", label: "生成图片", icon: Image },
  { to: "/create/video", label: "生成视频", icon: Film },
  { to: "/api-keys", label: "API 密钥", icon: KeyRound }
] as const;

const adminNavigation = [
  { to: "/admin", label: "运行概览", icon: Activity },
  { to: "/admin/users", label: "用户", icon: Users },
  { to: "/admin/providers", label: "上游", icon: ServerCog },
  { to: "/admin/models", label: "模型", icon: Boxes },
  { to: "/admin/pricing", label: "价格", icon: ReceiptText },
  { to: "/admin/routing", label: "视频路由", icon: GitBranch },
  { to: "/admin/tasks", label: "全部任务", icon: ShieldCheck },
  { to: "/admin/audit", label: "审计日志", icon: FileClock }
] as const;

function NavigationLink({ item, onNavigate }: { item: (typeof userNavigation)[number] | (typeof adminNavigation)[number]; onNavigate: () => void }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      activeOptions={{ exact: item.to === "/tasks" || item.to === "/admin" }}
      activeProps={{ "data-status": "active" }}
      className="nav-link"
      onClick={onNavigate}
    >
      <Icon size={17} strokeWidth={1.8} />
      <span>{item.label}</span>
    </Link>
  );
}

export function AppShell() {
  const { data: user } = useQuery(sessionQuery());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem("media-console-theme");
    return stored ? stored === "dark" : window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("media-console-theme", dark ? "dark" : "light");
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", dark ? "#111513" : "#f4f6f5");
  }, [dark]);

  useEffect(() => {
    document.documentElement.lang = "zh-CN";
    document.title = "API Media Console";
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", "管理图片与视频生成任务、API 密钥和卖家运营。");
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1023px)");
    const update = () => {
      setMobile(media.matches);
      if (!media.matches) setMenuOpen(false);
    };
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      const redirect = `${window.location.pathname}${window.location.search}`;
      queryClient.clear();
      void navigate({ to: "/login", search: { redirect }, replace: true });
    };
    window.addEventListener("media:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("media:unauthorized", handleUnauthorized);
  }, [navigate, queryClient]);

  const initials = useMemo(() => (user?.name || user?.username || "U").trim().slice(0, 1).toUpperCase(), [user]);
  const logout = useMutation({
    mutationFn: api.logout,
    onSettled: async () => {
      queryClient.clear();
      await navigate({ to: "/login", search: { redirect: undefined }, replace: true });
    }
  });

  return (
    <div className="app-frame">
      <div className="mobile-backdrop" data-open={menuOpen} onClick={() => setMenuOpen(false)} />
      <aside id="primary-navigation" className="sidebar" data-open={menuOpen} aria-label="主导航" aria-hidden={mobile && !menuOpen} inert={mobile && !menuOpen ? true : undefined}>
        <div className="sidebar-head">
          <div className="brand-mark"><Aperture size={17} strokeWidth={2} /></div>
          <strong>API Media</strong>
          <IconButton className="ml-auto mobile-menu" variant="ghost" title="关闭导航" aria-controls="primary-navigation" aria-expanded={menuOpen} onClick={() => setMenuOpen(false)}><X size={17} /></IconButton>
        </div>
        <nav>
          <div className="sidebar-section">
            <div className="sidebar-label">工作台</div>
            {userNavigation.map((item) => <NavigationLink key={item.to} item={item} onNavigate={() => setMenuOpen(false)} />)}
          </div>
          {user?.role === "admin" && (
            <div className="sidebar-section">
              <div className="sidebar-label">管理</div>
              {adminNavigation.map((item) => <NavigationLink key={item.to} item={item} onNavigate={() => setMenuOpen(false)} />)}
            </div>
          )}
        </nav>
      </aside>
      <main className="app-main">
        <header className="topbar">
          <div className="topbar-identity toolbar min-w-0">
            <IconButton className="mobile-menu" variant="ghost" title="打开导航" aria-controls="primary-navigation" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}><Menu size={18} /></IconButton>
            <div className="truncate text-xs text-[var(--text-muted)]">{user?.username}</div>
          </div>
          <div className="topbar-actions toolbar">
            <div className="topbar-points inline-flex h-8 items-center gap-1.5 rounded-[5px] border border-[var(--border)] px-2 text-xs font-semibold text-[var(--text-muted)]" title={`可用积分 ${formatPoints(user?.points.available)}`} aria-label={`可用积分 ${formatPoints(user?.points.available)}`}>
              <Coins className="flex-none" size={15} strokeWidth={1.8} />
              <span className="topbar-points-value mono truncate">{formatPoints(user?.points.available)}</span>
            </div>
            <IconButton variant="ghost" title={dark ? "切换浅色主题" : "切换深色主题"} onClick={() => setDark((value) => !value)}>
              {dark ? <Sun size={17} strokeWidth={1.8} /> : <Moon size={17} strokeWidth={1.8} />}
            </IconButton>
            <div className="grid h-7 w-7 place-items-center rounded-[6px] bg-[var(--surface-muted)] text-xs font-bold text-[var(--text-muted)]" aria-label={user?.name ?? user?.username}>{initials}</div>
            <IconButton variant="ghost" title="退出登录" disabled={logout.isPending} onClick={() => logout.mutate()}><LogOut size={17} strokeWidth={1.8} /></IconButton>
          </div>
        </header>
        <Outlet />
      </main>
    </div>
  );
}
